import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeatherModule } from 'angular-feather';
import { finalize } from 'rxjs';
import { DialogComponent } from '../dialog/dialog.component';
import { EditProductModalComponent, EditProductModalResult } from '../dialog/edit-product-modal/edit-product-modal.component';
import { ImagePreviewModalComponent } from '../dialog/image-preview-modal/image-preview-modal.component';
import { ShareSessionModalComponent } from '../dialog/share-session-modal/share-session-modal.component';
import { AdminImage } from '../services/api-services/admin-images.service';
import {
  SessionProduct,
  SharedSession,
  UserSession,
  UserSessionsService
} from '../services/api-services/user-sessions.service';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

@Component({
  selector: 'app-shared-sessions',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatIconModule,
    FeatherModule
  ],
  templateUrl: './shared-sessions.component.html',
  styleUrl: './shared-sessions.component.scss'
})
export class SharedSessionsComponent implements OnInit {
  @ViewChild('tableWrapper') private tableWrapperRef?: ElementRef<HTMLDivElement>;

  // ── Sessions list ───────────────────────────────────────────────────────────
  sessions: SharedSession[] = [];
  isLoadingSessions = false;
  sessionsErrorMessage: string | null = null;
  activeSessionId: number | null = null;

  readonly sessionColumns: string[] = [
    'name',
    'owner',
    'product_count',
    'created_at',
    'access',
    'status',
    'col_select',
    'col_view',
    'col_download',
    'col_manage',
    'col_delete'
  ];

  // ── Session detail (products) ───────────────────────────────────────────────
  selectedSession: SharedSession | null = null;
  products: SessionProduct[] = [];
  totalProducts = 0;
  isLoadingProducts = false;
  isInitialLoad = true;
  productErrorMessage: string | null = null;
  pageSize = PAGE_SIZE_OPTIONS[1];
  pageIndex = 0;

  readonly displayedColumns: (keyof AdminImage | 'delete' | 'edit' | 'actions' | 'uploaded_by')[] = [
    'image_name',
    'brand',
    'item',
    'material',
    'color',
    'product_age',
    'product_condition',
    'price',
    'uploaded_by',
    'edit',
    'actions',
    'delete'
  ];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  filtersForm: FormGroup;

  private rowDownloads = new Set<number>();
  private rowDeletes = new Set<number>();
  private rowPreviews = new Set<number>();
  private sessionActivating = new Set<number>();
  private sessionDeletes = new Set<number>();
  isDownloadingSessionCsv = false;

  private lastTouchY = 0;
  private lastTouchX = 0;
  private isHandlingVerticalTouch = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly sessionsService: UserSessionsService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar
  ) {
    this.filtersForm = this.fb.group({ brand: [''], item: [''] });
  }

  ngOnInit(): void {
    this.loadSessions();
    this.loadActiveSession();
  }

  private loadActiveSession(): void {
    this.sessionsService.getActiveSession().subscribe({
      next: response => { this.activeSessionId = response.session?.id ?? null; },
      error: () => { this.activeSessionId = null; }
    });
  }

  // ── Sessions list ───────────────────────────────────────────────────────────

  isActiveSession(sessionId: number): boolean {
    return this.activeSessionId === sessionId;
  }

  isActivatingSession(sessionId: number): boolean {
    return this.sessionActivating.has(sessionId);
  }

  toggleActiveSession(session: SharedSession): void {
    if (this.sessionActivating.has(session.id) || !session.can_activate) return;
    if (this.isActiveSession(session.id)) {
      this.deactivateSession(session);
    } else {
      this.activateSession(session);
    }
  }

  private activateSession(session: SharedSession): void {
    this.sessionActivating.add(session.id);
    this.sessionsService.setActiveSession(session.id)
      .pipe(finalize(() => this.sessionActivating.delete(session.id)))
      .subscribe({
        next: () => {
          this.activeSessionId = session.id;
          this.snackBar.open(`"${session.name}" selected — go to the Estimator to start adding products.`, 'Dismiss', {
            duration: 5000, horizontalPosition: 'end', verticalPosition: 'top'
          });
        },
        error: err => {
          this.snackBar.open('Failed to select session.', 'Dismiss', { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' });
          console.error(err);
        }
      });
  }

  private deactivateSession(session: SharedSession): void {
    this.sessionActivating.add(session.id);
    this.sessionsService.clearActiveSession()
      .pipe(finalize(() => this.sessionActivating.delete(session.id)))
      .subscribe({
        next: () => {
          this.activeSessionId = null;
          this.snackBar.open(`"${session.name}" deselected.`, 'Dismiss', {
            duration: 3000, horizontalPosition: 'end', verticalPosition: 'top'
          });
        },
        error: err => {
          this.snackBar.open('Failed to deselect session.', 'Dismiss', { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' });
          console.error(err);
        }
      });
  }

  loadSessions(): void {
    this.isLoadingSessions = true;
    this.sessionsErrorMessage = null;

    this.sessionsService.getSharedSessions()
      .pipe(finalize(() => (this.isLoadingSessions = false)))
      .subscribe({
        next: sessions => {
          this.sessions = sessions;
          if (this.selectedSession) {
            const refreshed = sessions.find(session => session.id === this.selectedSession?.id);
            if (refreshed) {
              this.selectedSession = refreshed;
            } else {
              this.selectedSession = null;
              this.products = [];
              this.totalProducts = 0;
            }
          }
        },
        error: err => {
          this.sessionsErrorMessage = 'Unable to load shared sessions.';
          this.snackBar.open('Unable to load shared sessions.', 'Dismiss', { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' });
          console.error(err);
        }
      });
  }

  selectSession(session: SharedSession): void {
    this.selectedSession = session;
    this.pageIndex = 0;
    this.filtersForm.reset({ brand: '', item: '' });
    this.isInitialLoad = true;
    this.loadProducts();
  }

  backToSessions(): void {
    this.selectedSession = null;
    this.products = [];
    this.totalProducts = 0;
    this.isInitialLoad = true;
    this.productErrorMessage = null;
    this.loadSessions();
  }

  openShareModal(session: SharedSession): void {
    if (!session.can_manage_sharing) return;
    // Cast to UserSession shape (compatible fields) for the modal
    const asUserSession: UserSession = {
      id: session.id,
      name: session.name,
      created_at: session.created_at,
      product_count: session.product_count,
      visibility: session.visibility,
      is_public: session.is_public,
      shared_with_count: session.shared_with_count
    };
    const dialogRef = this.dialog.open(ShareSessionModalComponent, {
      width: '640px',
      maxWidth: '95vw',
      data: { session: asUserSession, isOwner: true }
    });
    dialogRef.afterClosed().subscribe(changed => {
      if (changed) this.loadSessions();
    });
  }

  openDeleteSessionDialog(session: SharedSession): void {
    if (!session.can_delete_session || this.isDeletingSession(session.id)) return;
    const dialogRef = this.dialog.open(DialogComponent, {
      width: '520px',
      data: {
        dialogTitle: 'Delete session',
        content: `Are you sure you want to delete the session "${session.name}"? Its products will remain in the full product library.`,
        newButton: true,
        newButtonName: 'Delete',
        newBtnColor: 'warn',
        createFile: false
      }
    });
    dialogRef.componentInstance.buttonClicked.subscribe(() => {
      dialogRef.close();
      this.deleteSession(session);
    });
  }

  private deleteSession(session: SharedSession): void {
    this.sessionDeletes.add(session.id);
    this.sessionsService.deleteSession(session.id)
      .pipe(finalize(() => this.sessionDeletes.delete(session.id)))
      .subscribe({
        next: () => {
          this.sessions = this.sessions.filter(item => item.id !== session.id);
          if (this.selectedSession?.id === session.id) this.selectedSession = null;
          if (this.activeSessionId === session.id) this.activeSessionId = null;
          this.snackBar.open(`Session "${session.name}" deleted.`, 'Dismiss', {
            duration: 3000, horizontalPosition: 'end', verticalPosition: 'top'
          });
        },
        error: () => this.snackBar.open('Failed to delete session.', 'Dismiss', {
          duration: 4000, horizontalPosition: 'end', verticalPosition: 'top'
        })
      });
  }

  isDeletingSession(sessionId: number): boolean {
    return this.sessionDeletes.has(sessionId);
  }

  audienceLabel(session: SharedSession): string {
    if (session.is_public) {
      if (session.is_owner) return 'Public';
      return session.can_write ? 'Public · Contributor' : 'Public · Read-only';
    }
    if (session.is_owner) {
      const count = session.shared_with_count;
      return `Shared with ${count} ${count === 1 ? 'person' : 'people'}`;
    }
    return session.can_write ? 'Contributor' : 'Read-only';
  }

  downloadSessionCsv(session: SharedSession): void {
    this.isDownloadingSessionCsv = true;
    this.sessionsService.downloadSessionCsv(session.id)
      .pipe(finalize(() => (this.isDownloadingSessionCsv = false)))
      .subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${session.name}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: err => {
          this.snackBar.open('Failed to download CSV.', 'Dismiss', { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' });
          console.error(err);
        }
      });
  }

  // ── Products (detail view) ──────────────────────────────────────────────────

  applyFilters(): void {
    this.pageIndex = 0;
    this.loadProducts();
  }

  resetFilters(): void {
    this.filtersForm.reset({ brand: '', item: '' });
    this.pageIndex = 0;
    this.loadProducts();
  }

  handlePageEvent(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadProducts();
  }

  private loadProducts(): void {
    if (!this.selectedSession) return;

    const { brand, item } = this.filtersForm.value;
    this.isLoadingProducts = true;
    this.productErrorMessage = null;

    this.sessionsService
      .getSessionProducts(this.selectedSession.id, {
        limit: this.pageSize,
        offset: this.pageIndex * this.pageSize,
        brand: brand?.trim() || undefined,
        item: item?.trim() || undefined
      })
      .pipe(finalize(() => { this.isLoadingProducts = false; this.isInitialLoad = false; }))
      .subscribe({
        next: response => {
          this.products = response.products as SessionProduct[];
          this.totalProducts = response.total;
        },
        error: err => {
          this.products = [];
          this.totalProducts = 0;
          this.productErrorMessage = 'Unable to load session products.';
          this.snackBar.open('Unable to load session products.', 'Dismiss', { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' });
          console.error(err);
        }
      });
  }

  isDownloading(imageId: number): boolean {
    return this.rowDownloads.has(imageId);
  }

  isDeleting(imageId: number): boolean {
    return this.rowDeletes.has(imageId);
  }

  isPreviewing(imageId: number): boolean {
    return this.rowPreviews.has(imageId);
  }

  downloadImage(image: AdminImage): void {
    if (!image?.id || this.isDownloading(image.id)) return;
    this.rowDownloads.add(image.id);
    this.sessionsService
      .getSessionProductDownloadUrl(image.id)
      .pipe(finalize(() => this.rowDownloads.delete(image.id)))
      .subscribe({
        next: response => window.open(response.download_url, '_blank', 'noopener'),
        error: () => this.snackBar.open('Failed to fetch download link.', 'Dismiss', { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' })
      });
  }

  previewImage(image: AdminImage): void {
    if (!image?.id || this.isPreviewing(image.id)) return;
    this.rowPreviews.add(image.id);
    this.sessionsService
      .getSessionProductDownloadUrl(image.id)
      .pipe(finalize(() => this.rowPreviews.delete(image.id)))
      .subscribe({
        next: response => {
          const name = image.image_name?.split('/')?.pop() ?? image.image_name ?? 'Image';
          this.dialog.open(ImagePreviewModalComponent, {
            width: '860px',
            maxWidth: '95vw',
            data: { url: response.download_url, name }
          });
        },
        error: () => this.snackBar.open('Failed to fetch image preview.', 'Dismiss', { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' })
      });
  }

  // ── Own-product permission helpers ──────────────────────────────────────────

  canEditProduct(image: SessionProduct): boolean {
    return image.can_edit;
  }

  canDeleteProduct(image: SessionProduct): boolean {
    return image.can_delete;
  }

  openEditProductDialog(product: SessionProduct): void {
    if (!product?.id) return;
    const dialogRef = this.dialog.open(EditProductModalComponent, {
      width: '680px',
      maxWidth: '95vw',
      data: { product, title: 'Edit Product' }
    });
    dialogRef.afterClosed().subscribe((result: EditProductModalResult | null) => {
      if (!result) return;
      this.sessionsService.editProduct(product.id, result).subscribe({
        next: response => {
          const idx = this.products.findIndex(p => p.id === product.id);
          if (idx !== -1) {
            this.products[idx] = {
              ...response.product,
              uploaded_by: product.uploaded_by,
              uploaded_by_username: product.uploaded_by_username,
              can_edit: product.can_edit,
              can_delete: product.can_delete
            };
            this.products = [...this.products];
          }
          this.snackBar.open('Product updated.', 'Dismiss', { duration: 3000, horizontalPosition: 'end', verticalPosition: 'top' });
        },
        error: () => this.snackBar.open('Failed to update product.', 'Dismiss', { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' })
      });
    });
  }

  openDeleteProductDialog(image: SessionProduct): void {
    if (!image?.id || this.isDeleting(image.id)) return;
    const label = `${image.item?.trim() || 'Unknown item'} — ${image.brand?.trim() || 'Unknown brand'}`;
    const dialogRef = this.dialog.open(DialogComponent, {
      width: '520px',
      data: {
        dialogTitle: 'Remove from session',
        content: `Remove "${label}" from this session? The product will remain in the full product library.`,
        newButton: true,
        newButtonName: 'Remove',
        newBtnColor: 'warn',
        createFile: false
      }
    });
    dialogRef.componentInstance.buttonClicked.subscribe(() => {
      dialogRef.close();
      this.removeProductFromSession(image);
    });
  }

  private removeProductFromSession(image: SessionProduct): void {
    if (!this.selectedSession) return;
    this.rowDeletes.add(image.id);
    this.sessionsService
      .removeProductFromSession(this.selectedSession.id, image.id)
      .pipe(finalize(() => this.rowDeletes.delete(image.id)))
      .subscribe({
        next: () => {
          this.products = this.products.filter(p => p.id !== image.id);
          this.totalProducts = Math.max(0, this.totalProducts - 1);
          this.snackBar.open('Product removed from session.', 'Dismiss', { duration: 3000, horizontalPosition: 'end', verticalPosition: 'top' });
        },
        error: () => this.snackBar.open('Failed to remove product.', 'Dismiss', { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' })
      });
  }

  // ── Touch & wheel scroll helpers ─────────────────────────────────────────────

  onTableWheel(event: WheelEvent): void {
    if (!this.tableWrapperRef?.nativeElement) return;
    if (!this.tableWrapperRef.nativeElement.contains(event.target as Node)) return;
    const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaX !== 0);
    if (isHorizontal || event.deltaY === 0) return;
    event.preventDefault();
    this.scrollWindowBy(event.deltaY);
  }

  onTableTouchStart(event: TouchEvent): void {
    this.lastTouchY = event.touches[0]?.clientY ?? 0;
    this.lastTouchX = event.touches[0]?.clientX ?? 0;
    this.isHandlingVerticalTouch = false;
  }

  onTableTouchMove(event: TouchEvent): void {
    if (!this.tableWrapperRef?.nativeElement) return;
    const touch = event.touches[0];
    if (!touch) return;
    const deltaY = this.lastTouchY - touch.clientY;
    const deltaX = this.lastTouchX - touch.clientX;
    if (!this.isHandlingVerticalTouch) {
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this.lastTouchY = touch.clientY;
        this.lastTouchX = touch.clientX;
        return;
      }
      this.isHandlingVerticalTouch = true;
    }
    event.preventDefault();
    if (deltaY !== 0) this.scrollWindowBy(deltaY);
    this.lastTouchY = touch.clientY;
    this.lastTouchX = touch.clientX;
  }

  private scrollWindowBy(deltaY: number): void {
    if (typeof window === 'undefined') return;
    window.scrollBy({ top: deltaY, left: 0, behavior: 'auto' });
  }
}
